import type { Guide, GuideRecordHint } from '../types';

/**
 * S-03.11-02 — pełna struktura maila w skrzynce (Bartek 03.11.2026; scenariusz e2e 45).
 * Dane: wątek DEMO 046 z trzech maili (zapytanie klienta z DW i „odpowiedz do”, nasza odpowiedź z UDW,
 * odpowiedź klienta z obrazem w treści), mail DEMO 047 z załączonym mailem (.eml) i plikiem w nim oraz
 * mail DEMO 048 od nadawcy, który nie przeszedł SPF (`apps/api/prisma/demo-email-structure.ts`).
 * Nagrywarka tylko otwiera karty, niczego nie zapisuje.
 */
const THREAD: GuideRecordHint = {
  description: 'ostatni mail wątku DEMO 046 — ze skrzynki',
  listPath: '/emails',
};

const NESTED: GuideRecordHint = {
  description: 'mail DEMO 047 z załączonym mailem — ze skrzynki',
  listPath: '/emails',
};

const SUSPICIOUS: GuideRecordHint = {
  description: 'mail DEMO 048 od nadawcy bez weryfikacji — ze skrzynki',
  listPath: '/emails',
};

export const guide: Guide = {
  flagKey: 'emailDetails',
  title: 'Pełna struktura maila',
  whatItDoes:
    'Karta maila pokazuje, kto pisał i do kogo: od, wysłane w imieniu przez, do, DW, odpowiedz do — a ukrytą kopię tylko wtedy, gdy wolno ją zobaczyć. Widać datę wysłania i otrzymania, ważność i to, czy to odpowiedź automatyczna albo wysyłka masowa. Wątek zbiera wszystkie maile sprawy z przejściem do każdego. Załączniki są podzielone na pokazane w treści i osobne pliki, a pliki z wnętrza innych stoją pod nimi. Obrazy z sieci są zablokowane, dopóki ich nie pokażesz. Gdy nadawca nie przeszedł weryfikacji, karta ostrzega — mail jest dalej w skrzynce, decyzja należy do Ciebie.',
  audience: ['CS', 'Administracja'],
  presentationItems: ['S-03.11-02'],
  steps: [
    {
      route: '/emails',
      target: 'email-recipients-cell',
      title: 'Do kogo poszedł mail — już na liście',
      description:
        'Kolumna „Do/DW” pokazuje pierwszego odbiorcę i liczbę pozostałych, pełna lista jest w podpowiedzi. Filtry „Nadawca” i „Odbiorca” (Dodaj filtr) szukają po adresie albo nazwie — także w mailach zapisanych przed zmianą.',
      action: 'Dodaj filtr „Nadawca” i wpisz adres klienta.',
      recorder: { account: 'cs' },
    },
    {
      route: '/emails/:emailId/show',
      record: THREAD,
      target: 'email-details',
      title: 'Wszyscy uczestnicy z rolami',
      description:
        'Od, wysłane w imieniu przez, do, DW i „odpowiedz do” w kolejności z nagłówka maila. Ukrytą kopię (UDW) widzi tylko nadawca: pokazujemy ją przy mailu wysłanym z naszej skrzynki albo gdy ukrytą kopię dostała nasza skrzynka. Obok data wysłania i otrzymania, ważność i rodzaj maila.',
      action: 'Sprawdź, na jaki adres odpowiedzieć — „Odpowiedz do” bywa inny niż „Od”.',
      recorder: { account: 'cs', demo: { kind: 'scenario', key: 's45ThreadLastEmailId' } },
    },
    {
      route: '/emails/:emailId/show',
      record: THREAD,
      target: 'email-thread',
      title: 'Wątek sprawy z przejściem',
      description:
        'Maile z tej samej rozmowy — także te połączone tylko nagłówkiem odpowiedzi albo identyfikatorem rozmowy w skrzynce — po kolei, z nadawcą i datą. Bieżący jest wyróżniony, do pozostałych przechodzisz jednym kliknięciem.',
      action: 'Kliknij pierwszy mail wątku.',
      recorder: { account: 'cs', demo: { kind: 'scenario', key: 's45ThreadLastEmailId' } },
    },
    {
      route: '/emails/:emailId/show',
      record: THREAD,
      target: 'email-body',
      title: 'Treść bez skryptów, obrazy z sieci zablokowane',
      description:
        'Treść jest oczyszczona ze skryptów i formularzy. Obraz wklejony w treść (z załącznika) widać od razu; obrazy z sieci są zablokowane, bo nadawca mógłby po nich poznać, że mail otwarto. „Pokaż obrazy z sieci” włącza je dla tej karty.',
      action: 'Kliknij „Pokaż obrazy z sieci”, jeśli ufasz nadawcy.',
      recorder: { account: 'cs', demo: { kind: 'scenario', key: 's45ThreadLastEmailId' } },
    },
    {
      route: '/emails/:emailId/show',
      record: NESTED,
      target: 'email-attachments',
      title: 'Załączniki: w treści, osobno, w załączniku',
      description:
        'Każdy plik ma oznaczenie: pokazany w treści, osobny plik albo plik z wnętrza innego — na przykład PDF z maila przesłanego jako załącznik stoi wcięty pod tym mailem. Rodzaj pliku jest sprawdzany po zawartości.',
      action: 'Znajdź plik z wnętrza załączonego maila.',
      recorder: { account: 'cs', demo: { kind: 'scenario', key: 's45NestedEmailId' } },
    },
    {
      route: '/emails/:emailId/show',
      record: SUSPICIOUS,
      target: 'email-sender-unverified',
      title: 'Nadawca nie przeszedł weryfikacji',
      description:
        'Serwer poczty sprawdził nadawcę (SPF, DKIM, DMARC) i coś się nie zgadza — adres mógł zostać podrobiony. To informacja, nie blokada: mail zostaje w skrzynce, a Ty sprawdzasz u klienta, zanim na jego podstawie coś zmienisz.',
      action: 'Zadzwoń do klienta, jeśli mail zmienia zlecenie.',
      recorder: { account: 'cs', demo: { kind: 'scenario', key: 's45SuspiciousEmailId' } },
    },
  ],
};
